import { VisualizationTypes } from 'core/constants/visualizations';
import {
  ColumnDataTypes, DimensionMode, DimensionStatus, FieldEntitiesType,
} from 'core/constants/report';
import { FilterType } from 'core/constants/filter';
import {
  DrilldownConfigMapping, IFilterMetaInfo, ReportType as IReportType,
} from 'redux-v2/report-builder/report-builder-store.state';
import { FeatureContext, UserContext } from 'core/constants/common';
import {
  IChartVisualizationBuilder,
  IColumn,
  IColumnProperties,
  ICustomFilterDefinition,
  IFiltersMetaData,
  IReportBuilderConfig,
  IVisualizationBuilder,
} from '../report-response';
import { ObjModel } from '..';
import { IExpression } from '../report-expressions';
import { IPanelVisualisation } from '../dashboard';
import { IDimension } from '../report-redux';

export interface IRawTemplate {
  Namespace: string;
  TemplateId: string;
  Type: FieldEntitiesType;
  ReportConfig: IReportConfig;
  FilterMetaInfo: IFilterMetaInfo[];
  Settings: ITemplateSetting;
  TemplateMeta: ITemplateMeta;
  RawTemplateId?: string;
  Joins?: string;
}

export interface ITemplateSetting {
  PartitionColumnFilterDateLimit: number;
}

export interface ICustomHierarchyColumnNode {
  Column: IColumn | IDimension;
  NextColID: string | null;
  PrevColID: string | null;
}

export type ICustomHierarchy = {
  HierarchyID: string; // GUID
  HierarchyName: string;
  PrimaryColID: string;
  LeafColID: string;
  ColumnsByID: { [colId: string]: ICustomHierarchyColumnNode };
  HierarchyDescription?: string;
};

export interface IReportConfig {
  ReportConfigId: string;
  CategoryConfigId: string;
  DimensionsMetaFields: Array<IDimensionsMetaFields>;
  Industry: string;
  LSQReportId: string;
  DisableDrillDown: boolean;
  Version: string;
  Status: ReportBuilderStatus;
  Builder: Array<IBuilder>;
  SubBuilders: Array<IBuilder>;
  Paging: IPaging;
  CalculatedColumns: IBuilderMeasure[];
  EnabledOn: string;
  IsOpportunityRelated: boolean;
  Details: IDetails;
  Sorting: Array<ISorting>;
  Visualization: IPanelVisualisation;
  Columns: Array<IReportBuilderColumn>;
  FiltersMetadata?: IFiltersMetaData; // Filter Metadata will get appended while save, to track the user selected value
  ConfigMetadata?: IConfigMetaData;
  FeatureContext?: FeatureContext;
  UserContext?: UserContext;
  drilldownConfigMapping?: DrilldownConfigMapping;
  DrilldownConfigIdMapping?: DrilldownConfigMapping;
  enableDrilldown?: boolean;
  drilldownPagination?: {
    PageIndex: number,
    PageSize: number,
  }
  SourceInfo: {
    Id: string,
    Type: string
  },
  CustomHierarchies: ICustomHierarchy[];
  CustomFilterDefinitions?: ICustomFilterDefinition[];
}

export interface IBuilder {
  DisplayName?: string;
  Source?: QuerySourceType;
  Namespace: string;
  Select: IBuilderDimensionMeasure;
  Filter: IBuilderFilter;
  Hierarchy: IHierarchy;
  Joins?: Array<JoinItem>;
  QueryId?: string;
  Paging?: IPaging;
  Sorting?: ISorting[];
  CustomColumns: Array<IReportBuilderColumn>;
  Type: IReportType;
  CustomHierarchies?: ICustomHierarchy[];
  UnionConfig?: IUnionConfig;
}

export interface IBuilderDimensionMeasure {
  Dimensions: Array<IBuilderDimension>;
  Measures: Array<IBuilderMeasure>;
}

export interface IBuilderDimension {
  Id: string;
  Alias: string;
  DataType: ColumnDataTypes;
  IsTypeAlteringExpression: boolean;
  GroupByExpression: IExpression;
  IsCustomExpression: boolean;
  IsAdditional?: boolean;
  QueryId?: string;
  IsSourceCustomColumn?: boolean;
  RangeFill?: number;
  Description?: string;
}

export interface IBuilderMeasure {
  IsGrandTotal: boolean;
  MeasureExpression: IExpression;
  Alias: string;
  DataType: ColumnDataTypes;
  DrillDownSettings: IDrilldownSettings;
  IsCustomExpression: boolean;
  IsAdditional?: boolean;
  Id: string;
  QueryId?: string;
  IsSourceCustomColumn?: boolean;
  Description?: string;
}

export interface IDrilldownSettings {
  Paging: null;
  Sorting: null;
  Columns: null;
}

export interface IBuilderFilter {
  FilterGroup: Array<IFilterGroup>;
  GroupOperator: string;
  SubFilters: Array<IBuilderFilter>;
}

export interface IFilterGroup {
  LinkedDimensionAlias: string;
  IsAdditional: boolean;
  AlwaysEnabled: boolean;
  ID: string;
  Alias: string;
  DataType: ColumnDataTypes;
  Operator: string; // enum to be added
  Render: IFilterGroupRender;
  JoinID?: string;
  LinkedTo?: string;
  IsUserIDFilter?: boolean;
  LinkedDimensionId?: string;
  QueryId?: string;
  IsLargeOptionSetField?: boolean;
  IsPrimaryUserFilter?: boolean;
  DropdownMetaData?: IDropdownMetaData;
  AssociatedCustomFilterId?: string;
}

export interface IFilterGroupRender {
  ID: string;
  Type: FilterType;
  DefaultValue: any;
  Metadata?: ObjModel.Obj;
  LinkedTo: string;
  Label: string;
  StyleConfig: null;
  ShowInMainReport?: boolean
  LinkedQueryId?: string;
  DropdownMetaData?: IDropdownMetaData;
}

export interface IDimensionsMetaFields {
  Dimension: string;
  QueryId:string;
  Type: string;
  Fields: Array<IDimensionsMetaFieldItem>;
  Id: string;
}

export interface IDimensionsMetaFieldItem {
  SchemaName: string;
  Alias: string;
  Id: string;
}

export interface JoinItem {
  Type: JoinType;
  With: string;
  ID: string;
  On: IExpression;
  Primary?: string;
  IsCTE: boolean;
}

export enum JoinType {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  INNER = 'INNER',
  FULL = 'FULL',
  CROSS = 'CROSS',
  UNION = 'UNION',
  UNIONALL = 'UNION_ALL'
}

export enum ReportBuilderStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  PREVIEWQUERY = 'QUERY'
}

export enum QuerySourceType {
  Table = 'Table',
  Query = 'Query',
}

export interface IHierarchy {
  ManagerFilterId?: string;
  SubordinatesFilterId?: string;
  ToUse?: boolean;
  DimensionAlias?: string;
  HierarchyField?: string;
  DimensionId?: string;
}

export interface IPaging {
  PageIndex: number;
  PageSize: number;
}

export interface IDetails {
  Title: string;
  Description: string;
  Type: ReportType;
}

export enum ReportType {
  System = 'System',
  Custom = 'Custom',
}
export enum ReportSourceInfo{
  Report = 'Report',
  Template = 'Template'
}
export interface ISorting {
  Field: string;
  Direction: SortingOrderTypes;
  Id: string;
  DimensionAlias?: string;
  DimensionType?: string;
  DimensionDataType?: string;
  DimensionRelatedFilterId?: string;
  IsDimensionMetadataField?: false;
  DimensionMetadataFieldSchemaName?: string;
}

export enum SortingOrderTypes {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface IVisualization {
  Id: string;
  DisplayName: string;
  Type: VisualizationTypes;
  Builder: IVisualizationBuilder | IChartVisualizationBuilder;
}

export interface IReportBuilderColumn {
  Id: string;
  BuilderConfig?: IReportBuilderConfig;
  Name: string;
  Props: IColumnProperties;
}

export interface ICustomColumnProperties extends IColumnProperties {
  FilterValues?: ({
    DisplayName: string | number,
    Value: string | number
  })[] | null
}

export interface IReportBuilderCustomColumns extends Omit<IReportBuilderColumn, 'Props'> {
  Props: ICustomColumnProperties
}

export enum ReportBuilderTab {
  Data = 'Data',
  Filters = 'Filters',
  Charts = 'Charts',
  Drilldown = 'Drilldown',
}

export interface ISetGroupingPayload {
  sourceIndex: number;
  status: DimensionStatus;
  mode: DimensionMode;
  destinationIndex?: number;
  isSwapEnabled: boolean;
  customDimension?: IDimension
}

export interface ISetDimensionsBinningPayload {
  index: any;
  value: string;
}

export interface IConfigMetaData {
  namespaceTypes: Object;
  filterMetaInfo: IRawFilterMetaInfo;
}

export interface IRawFilterMetaInfo {
  partitionColumnFilterIds: string[]
}

export interface ITemplateMeta{
  TemplateTitle: string;
  GroupTitle: string[];
  Description: string[];
}

export enum RequestType {
  Namespace = 'Namespace',
  ReportId = 'ReportId',
  Query = 'Query',
}

export interface IReportMetaInfo {
  Namespaces: string[];
  RequestType: RequestType;
  FeatureContext: FeatureContext;
  QueryBuilderConfig: QueryBuilderConfig;
  Joins: JoinItem[];
  Type?: IReportType;
  UnionConfig?: IUnionConfig;
}

export interface QueryBuilderConfig {
  Builder: QueryBuilder[];
  SubBuilders: IBuilder[];
  FiltersMetadata: IFiltersMetaData;
  DimensionsMetaFields: Array<IDimensionsMetaFields>;
  ExistingCustomColumns?: IReportBuilderCustomColumns[];
}

export interface QueryBuilder {
  Namespace: string,
  Source: QuerySourceType,
  Joins: Array<JoinItem>,
  UnionConfig?: IUnionConfig;
  Select: IBuilderDimensionMeasure,
  Filter: IBuilderFilter,
  Sorting?: ISorting[],
  Paging?: IPaging,
  CustomColumns?: IReportBuilderCustomColumns[]
  Type: IReportType,
  Hierarchy: IHierarchy,
}

export type IReportMetaInfoV2 = Omit<IReportMetaInfo, 'Namespaces' | 'QueryBuilderConfig'> & {
  Ids: Array<string>
  Builder: QueryBuilder;
  SubBuilder: IBuilder[];
};

export interface IDropdownMetaData {
  DropdownSelectType: string;
  UserStatusType: string;
}

export interface IUnionConfig{
  Id: string,
  DisplayName: string,
  UnionWith: IUnionConfigArrayItem[]
}
export interface IUnionConfigArrayItem{
  Mode: string;
  Namespace: string,
  Source: string
}
